import { getBlockContent, type StreamingBlock } from "@storytell/streaming";
import { TbLoader2 } from "solid-icons/tb";
import { createMemo, For, Show } from "solid-js";
import { twMerge } from "tailwind-merge";
import { safeParseBlockJson } from "../../message/parseBlockContent";
import { useChatRuntime } from "../../runtime/ChatRuntime";
import { type ConnectSlackPayload, parseConnectSlackPayload } from "./connectSlackSchema";
import { SlackIcon } from "./slackIcon";

export { SlackIcon };

interface ConnectSlackRendererProps {
  block: StreamingBlock;
  // Drops the card's border and background so it reads as content inside an enclosing group.
  flat?: boolean;
}

// The backend swaps this in for a Slack search/read result when the user
// hasn't granted their per-user Slack token; the button starts the OAuth grant
// against the customer's own Slack app.
export const ConnectSlackRenderer = (props: ConnectSlackRendererProps) => {
  const rt = useChatRuntime();
  const conn = rt.ui?.useProviderConnect?.("slack");
  const isConnecting = () => conn?.isConnecting() ?? false;
  const connect = () => {
    rt.actions.trackFeature?.({ feature: "chat.connect_provider", action: "slack", via: "button" });
    if (conn) void conn.connect();
    else rt.actions.connectProvider?.("slack");
  };
  const canConnect = () => Boolean(conn || rt.actions.connectProvider);
  const error = () => conn?.error?.() ?? null;

  const rawContent = createMemo(
    () =>
      (props.block.finalContent?.type === "text" ? props.block.finalContent.text : undefined) ??
      getBlockContent(props.block.blockId)
  );

  const payload = createMemo<ConnectSlackPayload | null>(() => {
    const raw = rawContent();
    if (!raw) return null;
    const json = safeParseBlockJson(raw);
    if (!json) return null;
    return parseConnectSlackPayload(json);
  });

  return (
    <Show when={payload()}>
      {(data) => (
        <div
          class={twMerge(
            "@container rounded-lg border border-primary-border bg-panel p-4",
            props.flat && "rounded-none border-0 bg-transparent p-0"
          )}
        >
          <div class="flex items-start gap-3">
            <SlackIcon class="size-6 shrink-0" />
            <div class="min-w-0 flex-1">
              <h3 class="text-sm font-medium text-primary-text">{rt.t("connect_slack_card.title")}</h3>
              <p class="mt-1 text-xs text-muted-text">
                {data().reason === "missing_scope"
                  ? rt.t("connect_slack_card.body_missing_scope")
                  : rt.t("connect_slack_card.body_not_connected")}
              </p>
              <Show when={data().missingScopes?.length}>
                <div class="mt-2 flex flex-wrap items-center gap-1">
                  <span class="text-xs text-muted-text">{rt.t("connect_slack_card.missing_scopes_label")}</span>
                  <For each={data().missingScopes}>
                    {(scope) => (
                      <code class="rounded bg-inset px-1 font-mono text-[0.7rem] text-primary-text">{scope}</code>
                    )}
                  </For>
                </div>
              </Show>
            </div>
          </div>
          <Show when={canConnect()}>
            <button
              type="button"
              onClick={() => connect()}
              disabled={isConnecting()}
              aria-busy={isConnecting()}
              class="mt-3 flex w-full items-center justify-center gap-2 rounded-md bg-brand px-4 py-2 text-sm font-semibold text-on-brand transition-colors hover:bg-brand-hover disabled:cursor-not-allowed disabled:opacity-50 @sm:w-auto"
            >
              <Show when={!isConnecting()} fallback={<TbLoader2 class="size-4 animate-spin" />}>
                <SlackIcon class="size-4" />
              </Show>
              <span>{isConnecting() ? rt.t("common.connecting") : rt.t("connect_slack_card.button")}</span>
            </button>
          </Show>
          <Show when={error()}>
            <p class="mt-2 text-xs text-red-500" role="alert">
              {error()}
            </p>
          </Show>
          <Show when={rt.slots?.slackChannelsConnect}>
            <div class="mt-3">{rt.slots?.slackChannelsConnect?.()}</div>
          </Show>
        </div>
      )}
    </Show>
  );
};
